import { handleGoHome } from '@pypestream/design-system';
import { proStudioUrqlGql } from '@pypestream/api-services';
import { useTranslation } from '@pypestream/translations';
import {
  AccessSetupScreen,
  type AccessSetupScreenLabels,
} from '@pypestream/ui-patterns';
import { FC, useCallback, useMemo } from 'react';

import { useAccessSetup } from '../../hooks/use-access-setup';
import { checkForErrors } from '../../utils/check-for-errors';
import {
  extractUserProjects,
  type UserProject,
} from '../../utils/extract-user-projects';
import { buildMyAccountUrl } from '../../utils/my-account-url';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';

/**
 * The screen's text in Pro Studio's own translation scope. The screen itself
 * lives in `@pypestream/ui-patterns`, which holds no text — see
 * `AccessSetupScreenLabels`.
 */
const useAccessSetupLabels = (): AccessSetupScreenLabels => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      loading: {
        body: String(t('pro-studio/common:accessSetup.loading.body')),
        note: String(t('pro-studio/common:accessSetup.loading.note')),
      },
      failed: {
        headline: String(t('pro-studio/common:accessSetup.failed.headline')),
        body: String(t('pro-studio/common:accessSetup.failed.body')),
      },
      denied: {
        headline: String(t('pro-studio/common:accessSetup.denied.headline')),
        body: String(t('pro-studio/common:accessSetup.denied.body')),
      },
      actions: {
        myAccount: String(t('pro-studio/common:accessSetup.actions.myAccount')),
        retry: String(t('pro-studio/common:accessSetup.actions.retry')),
        home: String(t('pro-studio/common:accessSetup.actions.home')),
      },
    }),
    [t]
  );
};

/**
 * Performs the role assignment. urql resolves with `{ data, error }` and does
 * not throw, so a returned `error` (a `CombinedError` carrying the GraphQL error
 * codes) is turned into a throw here — `useAccessSetup` needs a rejection to
 * classify a denial from a transient failure.
 */
const assignProjectRoles = async (projectId: string): Promise<void> => {
  const { error } = await proStudioUrqlGql.assignProjectProductAdminRoles({
    projectId,
  });
  if (error) {
    throw error;
  }
};

/**
 * Re-reads the signed-in user's projects — the same query and extraction
 * `get-user-data.ts` uses to fill `ctx.userProjects` at boot. That invoke runs
 * once per session (a module-level flag), so it cannot be asked again.
 */
const loadUserProjects = async (userId: string): Promise<UserProject[]> => {
  const response = await proStudioUrqlGql.getUserProjects({ userId });
  checkForErrors(response);
  return extractUserProjects(response.data?.tenancy_users[0]);
};

interface AccessSetupRunnerProps {
  projectId: string;
  userId: string;
  onGranted: () => void;
  goToMyAccount: () => void;
  goHome: () => void;
}

const AccessSetupRunner: FC<AccessSetupRunnerProps> = ({
  projectId,
  userId,
  onGranted,
  goToMyAccount,
  goHome,
}) => {
  const reloadProjects = useCallback(() => loadUserProjects(userId), [userId]);

  const handleGranted = useCallback(
    (projects: readonly UserProject[]) => {
      // The machine leaves `accessDenied` on this event, which unmounts this
      // screen; the toast is shown by `Root` once the project has opened.
      sendProStudioEvent({
        type: 'projectAccessGranted',
        userProjects: [...projects],
      });
      onGranted();
    },
    [onGranted]
  );

  const { status, retry } = useAccessSetup({
    projectId,
    run: assignProjectRoles,
    reloadProjects,
    onGranted: handleGranted,
  });
  const labels = useAccessSetupLabels();

  // `granted` lasts only until the machine has left `accessDenied`; keep the
  // loader up so the screen does not flash.
  if (status === 'loading' || status === 'granted') {
    return (
      <AccessSetupScreen
        status="loading"
        labels={labels}
        onMyAccount={goToMyAccount}
      />
    );
  }

  if (status === 'failed') {
    return (
      <AccessSetupScreen
        status="failed"
        labels={labels}
        onMyAccount={goToMyAccount}
        onRetry={retry}
      />
    );
  }

  return (
    <AccessSetupScreen
      status="denied"
      labels={labels}
      onMyAccount={goToMyAccount}
      onHome={goHome}
    />
  );
};

interface AccessSetupProps {
  /** Arms the "Access granted" toast (`useAccessGrantedToastOnOpen`). */
  onGranted: () => void;
}

/**
 * PSTUDIO-4098. What Pro Studio renders instead of the page while the machine is
 * in `accessDenied`: it assigns the product-admin roles for the project in the
 * URL, waits for them to propagate, re-reads the user's projects and hands the
 * list back to the machine, which then loads the project. When the backend
 * refuses — or no project is named — it shows the no-access view instead.
 *
 * `Root` keys this component by project id, so a different project in the URL
 * starts a fresh cycle.
 */
export const AccessSetup: FC<AccessSetupProps> = ({ onGranted }) => {
  const { projectId, userId, defaultOrgId } = useProStudioCtxSelector(
    (ctx) => ({
      projectId: ctx.project?.id ?? '',
      userId: ctx.userInfo?.id ?? '',
      defaultOrgId: ctx.userInfo?.defaultOrgId,
    })
  );

  const goToMyAccount = useCallback(() => {
    window.location.href = buildMyAccountUrl(
      import.meta.env.FE_MANAGER_URL,
      defaultOrgId
    );
  }, [defaultOrgId]);

  const goHome = useCallback(() => {
    handleGoHome({ accountId: defaultOrgId });
  }, [defaultOrgId]);

  const labels = useAccessSetupLabels();

  // No project to set up access for (the user has no projects at all and none is
  // named): nothing to assign, so go straight to the no-access view rather than
  // firing a mutation that can only fail.
  if (!projectId) {
    return (
      <AccessSetupScreen
        status="denied"
        labels={labels}
        onMyAccount={goToMyAccount}
        onHome={goHome}
      />
    );
  }

  return (
    <AccessSetupRunner
      projectId={projectId}
      userId={userId}
      onGranted={onGranted}
      goToMyAccount={goToMyAccount}
      goHome={goHome}
    />
  );
};
